import React, { useState, useEffect } from 'react';
import { databaseService } from '@/services/databaseService';
import type { PublicGame } from '@/types';
import { UserProfile } from '@/types';
import { LoadingSpinnerIcon } from './icons';

interface LobbyScreenProps {
  userProfile: UserProfile | null;
  onJoinGame: (gameId: string) => void;
  onJoinRoom?: (roomId: string) => void; // Added for multiplayer room rejoining
  onCreateGame: () => void;
  onCreateCartridge?: () => void; // Direct cartridge creation
  onSignOut: () => void;
}

interface GameCreationModal {
  show: boolean;
  isPublic: boolean;
  sessionName: string;
}

const LobbyScreen: React.FC<LobbyScreenProps> = ({ 
  userProfile, onJoinGame, onJoinRoom, onCreateGame, onCreateCartridge, onSignOut
}) => {
  const [publicGames, setPublicGames] = useState<PublicGame[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generatingInvite, setGeneratingInvite] = useState<string | null>(null);
  const [inviteLinks, setInviteLinks] = useState<{ [gameId: string]: string }>({});
  const [gameCreationModal, setGameCreationModal] = useState<GameCreationModal>({
    show: false,
    isPublic: true,
    sessionName: ''
  });
  const [showJoinByCode, setShowJoinByCode] = useState(false);
  const [inviteCode, setInviteCode] = useState('');

  const loadPublicGames = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Get user's saved game sessions instead of participating games
      const sessions = await databaseService.getUserGameSessions();
      console.log('[LobbyScreen] Loaded user sessions:', sessions);
      
      // Transform sessions to match the expected PublicGame format
      const transformedGames = sessions.map(session => ({
        id: session.id,
        userProfile: userProfile || {
          name: 'Player',
          age: null,
          traits: [],
          mindset: null,
          stats: {
            health: { current: 100, max: 100 },
            spiritualEnergy: { current: 100, max: 100 },
            experience: 0,
            luck: 0,
            karma: 0
          },
          skills: [],
          inventory: []
        },
        archetype: session.cartridge_id || 'Unknown',
        playerCount: session.is_multiplayer ? 2 : 1,
        maxPlayers: session.is_multiplayer ? 4 : 1,
        faction: session.is_multiplayer ? 'Multiplayer Room' : 'Single Player'
      }));
      
      setPublicGames(transformedGames);
    } catch (err) {
      console.error('Error loading user game sessions:', err);
      setError('Failed to load your saved game sessions. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const generateInviteLink = async (gameId: string) => {
    if (inviteLinks[gameId]) {
      // If invite link already exists, copy it
      navigator.clipboard.writeText(inviteLinks[gameId]);
      return;
    }

    setGeneratingInvite(gameId);
    try {
      const inviteLink = await databaseService.createGameInviteLink(gameId);
      setInviteLinks(prev => ({ ...prev, [gameId]: inviteLink }));
      navigator.clipboard.writeText(inviteLink);
    } catch (err) {
      console.error('Error generating invite link:', err);
      setError('Failed to generate invite link');
    } finally {
      setGeneratingInvite(null);
    }
  };

  const handleCreateGameClick = () => {
    setGameCreationModal({
      show: true,
      isPublic: true,
      sessionName: `${userProfile?.name || 'Player'}'s Session`
    });
  };

  const handleCreateGameConfirm = async () => {
    setGameCreationModal(prev => ({ ...prev, show: false }));
    
    // For now, just call the existing onCreateGame callback
    // In the future, this could save session preferences first
    onCreateGame();
  };

  const handleCancelGameCreation = () => {
    setGameCreationModal(prev => ({ ...prev, show: false }));
  };

  useEffect(() => {
    loadPublicGames();
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <header className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-orbitron text-cyan-400 neon-text-cyan">
            Dream Lobby
          </h1>
          <div className="flex items-center gap-4">
            <span className="text-slate-300">Welcome, {userProfile?.name || 'Dreamer'}</span>
            <button
              onClick={onSignOut}
              className="px-4 py-2 bg-slate-700 hover:bg-red-800 text-slate-200 font-medium rounded-lg transition-colors text-sm"
            >
              Sign Out
            </button>
          </div>
        </header>

        {error && (
          <div className="mb-6 p-4 bg-red-900/50 border border-red-500/50 rounded-lg text-red-200"
          >
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-1 space-y-6">
            <div className="glassmorphism p-6 rounded-xl border border-fuchsia-500/40 space-y-6">
              <h2 className="text-2xl font-orbitron text-fuchsia-400 mb-4">Start a New Dream</h2>
              <p className="text-slate-400 mb-4 text-sm">Create your own public or private session and invite others to join your journey.</p>
              <button
                onClick={handleCreateGameClick}
                className="w-full py-3 px-4 bg-gradient-to-r from-fuchsia-600 to-cyan-600 hover:from-fuchsia-500 hover:to-cyan-500 text-white font-semibold rounded-lg shadow-lg transform transition-all duration-200 hover:scale-105"
                data-testid="create-new-session"
              >
                Create New Session
              </button>
              <button
                onClick={onCreateGame}
                className="w-full py-3 px-4 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg shadow-lg transform transition-all duration-200 hover:scale-105"
                data-testid="browse-cartridges"
              >
                Browse Cartridges
              </button>
              {onCreateCartridge && (
                <button
                  onClick={onCreateCartridge}
                  className="w-full py-3 px-4 bg-purple-700 hover:bg-purple-600 text-white font-semibold rounded-lg shadow-lg transform transition-all duration-200 hover:scale-105"
                  data-testid="create-cartridge"
                >
                  Create New Cartridge
                </button>
              )}
            </div>
            
            {/* Join by invite code */}
            <div className="glassmorphism p-6 rounded-xl border border-cyan-500/40 space-y-4">
              <h2 className="text-xl font-orbitron text-cyan-400 mb-2">Join by Invite Code</h2>
              <p className="text-slate-400 text-sm">Have an invite code? Enter it below to join a multiplayer session.</p>
              
              {!showJoinByCode ? (
                <button
                  onClick={() => setShowJoinByCode(true)}
                  className="w-full py-3 px-4 bg-cyan-700 hover:bg-cyan-600 text-white font-semibold rounded-lg shadow-lg transform transition-all duration-200 hover:scale-105"
                  data-testid="show-join-by-code"
                >
                  Join Game
                </button>
              ) : (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    placeholder="Enter 6-character code"
                    maxLength={6}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-200 focus:border-cyan-500 focus:outline-none text-center text-lg tracking-wider"
                    data-testid="invite-code-input"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setShowJoinByCode(false);
                        setInviteCode('');
                      }}
                      className="flex-1 py-2 px-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        if (inviteCode.length === 6 && onJoinRoom) {
                          onJoinRoom(inviteCode);
                        }
                      }}
                      disabled={inviteCode.length !== 6}
                      className="flex-1 py-2 px-3 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="join-by-code-button"
                    >
                      Join
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-orbitron text-slate-100">
                Your Active Sessions
              </h2>
              <button
                onClick={loadPublicGames}
                disabled={isLoading}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors text-sm disabled:opacity-50"
              >
                {isLoading ? <LoadingSpinnerIcon className="w-5 h-5" /> : 'Refresh'}
              </button>
            </div>
            
            <div className="space-y-4">
              {isLoading && publicGames.length === 0 ? (
                <p className="text-slate-500 text-center py-8">Searching for active dream waves...</p>
              ) : publicGames.length === 0 ? (
                <div className="text-center py-8 text-slate-500 glassmorphism rounded-lg">
                  <p className="text-lg">No active sessions found.</p>
                  <p className="text-sm">Create a new dream to start your adventure!</p>
                </div>
              ) : (
                publicGames.map(game => (
                  <div key={game.id} className="glassmorphism p-4 rounded-lg border border-slate-700 hover:border-cyan-500 transition-colors">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-100">{game.userProfile.name}'s Dream</h3>
                        <p className="text-sm text-slate-400">
                          Archetype: {game.archetype} | Players: {game.playerCount} / {game.maxPlayers}
                          {game.faction && <span className="ml-2 text-xs text-cyan-400">({game.faction})</span>}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => generateInviteLink(game.id)}
                          disabled={generatingInvite === game.id}
                          className="px-4 py-2 bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
                        >
                          {generatingInvite === game.id ? 'Generating...' : 'Copy Invite'}
                        </button>
                        <button
                          onClick={() => {
                            if (game.faction === 'Multiplayer Room' && onJoinRoom) {
                              onJoinRoom(game.id);
                            } else {
                              onJoinGame(game.id);
                            }
                          }}
                          className="px-6 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-lg shadow-lg transition-colors"
                        >
                          {game.faction === 'Multiplayer Room' ? 'Rejoin Room' : 'Resume Game'}
                        </button>
                      </div>
                    </div>
                    {inviteLinks[game.id] && (
                      <div className="mt-3 p-3 bg-slate-800 rounded-lg">
                        <p className="text-xs text-slate-400 mb-2">Invite Link (copied to clipboard):</p>
                        <code className="text-xs text-amber-400 break-all">{inviteLinks[game.id]}</code>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Game Creation Modal */}
        {gameCreationModal.show && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md">
              <h3 className="text-xl font-orbitron text-cyan-400 mb-4">Create New Session</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Session Name
                  </label>
                  <input
                    type="text"
                    value={gameCreationModal.sessionName}
                    onChange={(e) => setGameCreationModal(prev => ({ ...prev, sessionName: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-200 focus:border-cyan-500 focus:outline-none"
                    placeholder="Enter session name..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Visibility
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        checked={gameCreationModal.isPublic}
                        onChange={() => setGameCreationModal(prev => ({ ...prev, isPublic: true }))}
                        className="mr-3 text-cyan-500"
                      />
                      <span className="text-slate-200">Public - Others can discover and join</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        checked={!gameCreationModal.isPublic}
                        onChange={() => setGameCreationModal(prev => ({ ...prev, isPublic: false }))}
                        className="mr-3 text-cyan-500"
                      />
                      <span className="text-slate-200">Private - Invite only</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleCancelGameCreation}
                  className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateGameConfirm}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-fuchsia-600 to-cyan-600 hover:from-fuchsia-500 hover:to-cyan-500 text-white font-semibold rounded-lg transition-colors"
                >
                  Create Session
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LobbyScreen;
